import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Store, ChevronDown } from 'lucide-react';

import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

import { merchantApi } from '../lib/api/merchantApi';
import { Merchant } from '../lib/api/types';

interface MerchantSelectorProps {
  selectedMerchantId?: string;
  onMerchantSelect: (merchant: Merchant | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  partnerId?: string; // Optional filter by partner
}

export const MerchantSelector: React.FC<MerchantSelectorProps> = ({
  selectedMerchantId,
  onMerchantSelect,
  label = 'Merchant',
  placeholder = 'Select a merchant',
  required = false,
  disabled = false,
  error,
  partnerId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Query to fetch all merchants
  const {
    data: merchantData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['merchants-admin', partnerId],
    queryFn: () => merchantApi.getAllMerchantsAdmin(partnerId),
  });

  const merchants = merchantData?.merchants || [];
  const selectedMerchant = merchants.find(m => m.id === selectedMerchantId);

  const handleMerchantSelect = (merchant: Merchant | null) => {
    onMerchantSelect(merchant);
    setIsOpen(false);
  };

  if (queryError) {
    return (
      <div className="space-y-2">
        {label && (
          <Label className={required ? "after:content-['*'] after:ml-0.5 after:text-destructive" : ""}>
            {label}
          </Label>
        )}
        <div className="text-sm text-destructive">
          Failed to load merchants
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label className={required ? "after:content-['*'] after:ml-0.5 after:text-destructive" : ""}>
          {label}
        </Label>
      )}

      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={`w-full justify-between ${
            error ? 'border-destructive' : ''
          } ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
        >
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4" />
            <span className={selectedMerchant ? '' : 'text-muted-foreground'}>
              {isLoading
                ? 'Loading merchants...'
                : selectedMerchant
                ? `${selectedMerchant.merchantName} (${selectedMerchant.accountNumber})`
                : placeholder}
            </span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} />
        </Button>

        {isOpen && !disabled && (
          <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto">
            <CardContent className="p-0">
              {/* Clear selection option */}
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-muted transition-colors border-b"
                onClick={() => handleMerchantSelect(null)}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4" /> {/* Spacer */}
                  <span className="text-muted-foreground">No merchant selected</span>
                </div>
              </button>

              {/* Merchant options */}
              {merchants.length === 0 ? (
                <div className="px-3 py-4 text-center text-muted-foreground">
                  No merchants available
                </div>
              ) : (
                merchants.map((merchant) => (
                  <button
                    key={merchant.id}
                    type="button"
                    className={`w-full px-3 py-2 text-left hover:bg-muted transition-colors ${
                      merchant.id === selectedMerchantId ? 'bg-muted' : ''
                    }`}
                    onClick={() => handleMerchantSelect(merchant)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <div>
                        <div className="font-medium">{merchant.merchantName}</div>
                        <div className="text-sm text-muted-foreground">
                          Account: {merchant.accountNumber}
                          {merchant.partnerName && ` • ${merchant.partnerName}`}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
